import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { Sparkles, ArrowLeft } from "lucide-react";
import { toast } from "sonner";

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const u = await login(email, password);
      toast.success(`Welcome back, ${u.name}`);
      if (u.role === "admin") nav("/admin");
      else nav("/dashboard");  // OnboardingGate handles redirection
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#05050A] text-white grid-bg flex items-center justify-center p-6">
      <div className="absolute top-6 left-6">
        <Link
          to="/"
          data-testid="auth-back-home"
          className="inline-flex items-center gap-2 text-sm text-white/60 hover:text-white"
        >
          <ArrowLeft className="h-4 w-4" /> Back to home
        </Link>
      </div>

      <div className="w-full max-w-md glass rounded-2xl p-8 fade-in-up">
        <div className="flex items-center gap-2 mb-8">
          <div className="h-9 w-9 rounded-lg brand-gradient grid place-items-center">
            <Sparkles className="h-5 w-5" />
          </div>
          <span className="font-heading font-black text-lg tracking-tight">
            MakeYour<span className="brand-gradient-text">Brand.IN</span>
          </span>
        </div>
        <h1 className="font-heading text-3xl font-black tracking-tight mb-2">Welcome back</h1>
        <p className="text-sm text-white/50 mb-8">Sign in to your workspace.</p>

        <form onSubmit={submit} className="space-y-4">
          <div>
            <label className="text-xs uppercase tracking-[0.2em] text-white/40">Email</label>
            <input
              type="email"
              data-testid="login-email-input"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="mt-2 w-full bg-white/5 border border-white/10 focus:border-orange-500/60 outline-none rounded-lg px-4 py-3 text-sm"
              placeholder="you@brand.com"
            />
          </div>
          <div>
            <label className="text-xs uppercase tracking-[0.2em] text-white/40">Password</label>
            <input
              type="password"
              data-testid="login-password-input"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              className="mt-2 w-full bg-white/5 border border-white/10 focus:border-orange-500/60 outline-none rounded-lg px-4 py-3 text-sm"
              placeholder="••••••••"
            />
          </div>
          <button
            type="submit"
            data-testid="login-submit-button"
            disabled={loading}
            className="w-full brand-gradient px-5 py-3 rounded-lg font-semibold text-sm hover:opacity-90 transition glow-orange disabled:opacity-50"
          >
            {loading ? "Signing in…" : "Sign in"}
          </button>
        </form>

        <div className="mt-6 text-sm text-white/50 text-center">
          New here?{" "}
          <Link to="/register" data-testid="login-register-link" className="brand-gradient-text font-semibold">
            Create an account
          </Link>
        </div>
      </div>
    </div>
  );
}
